import React, { useState, useMemo } from "react";

const GenericTable = ({
  columns,
  data,
  onSort,
  onRowClick,
  customRenderers,
  selectedRows,
  setSelectedRows,
  handleEdit,
  handleDelete,
  headerClassName = "bg-gray-500",
  rowsPerPageOptions = [5, 10, 20, 25, 30],
  emptyStateMessage = "No data available",  
  showPagination = true,
  rowKey = "id",
  stripedRows = false,
  hoverEffect = true
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // const [selectedRows, setSelectedRows] = useState([]);


  const renderCell = (row, column, rowIndex) => {
    const key = column.key;
  
    if (key === "sno") {
      return (currentPage - 1) * rowsPerPage + rowIndex + 1;
    }
  
    if (customRenderers?.[key]) {
      return customRenderers[key](row, {
        handleEdit,
        handleDelete,
        selectedRows,
        setSelectedRows
      });
    }
  
    return row[key] ?? "-";
  };
  

  // Memoized sorted data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];
      
      if (valueA === valueB) return 0;
      
      return sortConfig.direction === "asc" 
        ? valueA > valueB ? 1 : -1 
        : valueA < valueB ? 1 : -1;
    });
  }, [data, sortConfig]);

  // Pagination calculations
  const { paginatedData, totalPages } = useMemo(() => {
    const total = Math.ceil(sortedData.length / rowsPerPage);
    const paginated = showPagination 
      ? sortedData.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage
        )
      : sortedData;
    
    return { paginatedData: paginated, totalPages: total };
  }, [sortedData, currentPage, rowsPerPage, showPagination]);

  const handleSort = (key) => {
    const isSameKey = sortConfig.key === key;
    const direction = isSameKey && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    onSort?.(key, direction);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getRowClass = (index) => {
    let classes = "border";
    if (hoverEffect) classes += " hover:bg-gray-50";
    if (stripedRows && index % 2 === 0) classes += " bg-gray-100";
    return classes;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-gray-300">
        <thead className={headerClassName}>
          <tr>
            {columns.map(({ key, label, sortable, width, align }) => (
              <th
                key={key}
                onClick={() => sortable && handleSort(key)}
                className={`p-3 border cursor-pointer ${sortable ? "hover:bg-gray-300" : ""}`}
                style={{
                  width: width || "auto",
                  textAlign: align || "left"
                }}
              >
                <div className="flex items-center">
                  {label}
                  {sortable && (
                    <span className="ml-1">
                      {sortConfig.key === key
                        ? sortConfig.direction === "asc"
                          ? "↑"
                          : "↓"
                        : "↕"}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
              <tr
                key={row[rowKey] || rowIndex}
                className={getRowClass(rowIndex)}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map(({ key, align }) => (
                  <td 
                    key={key} 
                    className="p-3 border"
                    style={{ textAlign: align || "left" }}
                  >
                    {renderCell(row, { key }, rowIndex)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="p-4 text-center text-gray-500">
                {emptyStateMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {showPagination && data.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center p-3 border-t gap-3">
          <div className="flex items-center">
            <label className="mr-2">Rows per page:</label>
            <select
              className="border rounded p-1"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 border rounded bg-button-bg hover:bg-gray-800 disabled:opacity-70"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              «
            </button>
            <button
              className="px-3 py-1 border rounded bg-button-bg hover:bg-gray-800 disabled:opacity-70"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            
            <span className="px-2">
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              className="px-3 py-1 border rounded bg-button-bg hover:bg-gray-800 disabled:opacity-70"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
            <button
              className="px-3 py-1 border rounded bg-button-bg hover:bg-gray-800 disabled:opacity-70"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericTable;